import React, { useState, useEffect, useRef, useCallback } from "react";
import { showCockpitToast } from "../app/common/cockpit_toast";
import SocketMqttBridge from "../interface/socket_mqtt_bridge";
import { useMergedVehicleList } from "../hooks/useMergedVehicleList";
import { useTransportLogin } from "../hooks/useTransportLogin";
import { useWheelMenuNav, type WheelMenuEvent } from "../hooks/useWheelMenuNav";
import type { ExternalDisplayRoleStatus } from "../app/display/external_display_types";
import { UI_VERSION } from "../config/ui_version";
import { reloadUi } from "../utils/reload_ui";
import { unlockCockpitAudio } from "../app/sound/cockpit_audio_context";
import { FleetSelect } from "./components/fleet_select";
import { DeviceSetup } from "./components/device_setup";
import { WheelHelpOverlay } from "./components/wheel_help_overlay";

const LOGIN_VEHICLE_REFRESH_MS = 5000;

// "ID·비밀번호 저장" 사용자 설정값을 브라우저 재시작 후에도 유지하기 위한 localStorage 키.
// (useTransportLogin 의 sessionStorage 동일-세션 복원과는 별개의, 사용자가 직접 켜는 영구 저장.)
const REMEMBER_LOGIN_FLAG_KEY = "aw_remember_login";
const REMEMBER_LOGIN_ID_KEY = "aw_remember_id";
const REMEMBER_LOGIN_PW_KEY = "aw_remember_pw";

// localStorage 접근을 try/catch 로 감싸는 헬퍼 — 시크릿 모드 등에서 throw 될 수 있음.
const rememberedCredentialStore = {
  read(): { userId: string; password: string } | null {
    try {
      if (localStorage.getItem(REMEMBER_LOGIN_FLAG_KEY) !== "true") {
        return null;
      }
      const userId = localStorage.getItem(REMEMBER_LOGIN_ID_KEY) || "";
      const password = localStorage.getItem(REMEMBER_LOGIN_PW_KEY) || "";
      if (!userId && !password) {
        return null;
      }
      return { userId, password };
    } catch {
      return null;
    }
  },
  save(userId: string, password: string): void {
    try {
      localStorage.setItem(REMEMBER_LOGIN_FLAG_KEY, "true");
      localStorage.setItem(REMEMBER_LOGIN_ID_KEY, userId);
      localStorage.setItem(REMEMBER_LOGIN_PW_KEY, password);
    } catch {
      // no-op
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(REMEMBER_LOGIN_FLAG_KEY);
      localStorage.removeItem(REMEMBER_LOGIN_ID_KEY);
      localStorage.removeItem(REMEMBER_LOGIN_PW_KEY);
    } catch {
      // no-op
    }
  },
};

interface LoginScreenProps {
  bridge: SocketMqttBridge;
  onVehicleSessionConnected?: () => void;
  // 로그인 버튼(제스처) 시 다중 모니터 점유 + 장치 권한 일괄 획득 트리거.
  onLoginGesture?: () => void;
  // 수동 "디스플레이 펼치기" — 사용자 제스처 컨텍스트에서 보조 모니터 팝업을 (재)연다.
  // 브라우저는 팝업에 사용자 제스처를 요구하므로, 자동 열기가 차단됐을 때의 명시적 폴백.
  onReopenDisplays?: () => void;
  // 아직 못 연(팝업 차단 등) 보조 디스플레이가 있어 "다시 펼치기" 버튼을 강조할지 여부.
  showReopenDisplays?: boolean;
  // 장치 준비 단계용 — 보조 모니터(외부 디스플레이) 지원 여부(다중 모니터 환경인지).
  displaysSupported?: boolean;
  // 장치 준비 단계용 — 펼쳐야 할 보조 모니터가 모두 펼쳐졌는지(또는 펼칠 게 없는지).
  displaysReady?: boolean;
  // 장치 준비 단계용 — role(좌/우)별 열림+전체화면 세부 상태(행 분리 표시용).
  displayStatuses?: ExternalDisplayRoleStatus[];
  // 장치 준비 단계용 — 보조 모니터 (재)펼치기(사용자 제스처 컨텍스트).
  onOpenDisplays?: () => void;
}

function requestAppFullscreen(): void {
  if (typeof document === "undefined") {
    return;
  }
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  if (document.fullscreenElement) {
    return;
  }
  try {
    const req = el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.();
    if (req && typeof (req as Promise<void>).catch === "function") {
      (req as Promise<void>).catch(() => {
        // 사용자/브라우저가 거부하면 무시 — 콕핏 내 전체화면 버튼으로 재시도 가능.
      });
    }
  } catch {
    // no-op
  }
}

const LoginScreenComponent = (props: LoginScreenProps) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectionError, setSelectionError] = useState<string | null>(null);
  // 휠/게임패드 연결 여부 — 로그인 화면 힌트 강조용.
  const [wheelConnected, setWheelConnected] = useState(false);
  // 장치 준비 단계 완료 여부(세션 한정 컴포넌트 상태). 로그인↔콕핏 왕복은 유지되지만
  // 로그아웃 시 false 로 리셋해 재로그인 때 다시 장치 준비 단계를 거치게 한다.
  const [setupDone, setSetupDone] = useState(false);
  // 저장된 자격증명이 있으면 체크박스를 켠 상태로 시작.
  const [rememberCredentials, setRememberCredentials] = useState(
    () => rememberedCredentialStore.read() != null,
  );
  const {
    authenticatedCredentials,
    credentials,
    setCredentials,
    isLoggingIn,
    loginError,
    login,
    restoreStoredSession,
    clearStoredSession,
    resetLoginState,
  } = useTransportLogin({
    bridge: props.bridge,
  });

  const loginId = authenticatedCredentials?.userId || credentials.userId;
  const isAuthenticated = Boolean(authenticatedCredentials);

  const {
    vehicleList,
    signalingStatus,
    mqttStatus,
    signalingError,
    mqttError,
    reload,
  } = useMergedVehicleList(isAuthenticated, props.bridge);

  const selectedVehicle =
    vehicleList.find((vehicle) => vehicle.vehicleId === selectedVehicleId) || null;

  useEffect(() => {
    if (!selectedVehicleId) {
      return;
    }

    const stillVisible = vehicleList.some(
      (vehicle) => vehicle.vehicleId === selectedVehicleId,
    );
    if (!stillVisible) {
      setSelectedVehicleId("");
    }
  }, [selectedVehicleId, vehicleList]);

  // 느린 네트워크에서 이전 reload() 가 아직 진행 중일 때 새 인터벌 틱이 겹치지 않도록
  // in-flight 가드를 사용 — 동시 요청 누적 방지.
  const reloadInFlightRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      if (reloadInFlightRef.current) return;
      reloadInFlightRef.current = true;
      void reload().finally(() => {
        reloadInFlightRef.current = false;
      });
    }, LOGIN_VEHICLE_REFRESH_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [reload, isAuthenticated]);

  useEffect(() => {
    restoreStoredSession();
  }, [restoreStoredSession]);

  // 미인증 상태로 진입할 때마다(최초 마운트 + 로그아웃/뒤로가기 복귀) 저장된 자격증명을 프리필.
  // (입력 필드가 비어있을 때만 — 사용자가 이미 타이핑한 값은 덮어쓰지 않음.)
  // 이전엔 마운트 1회만 해서, 뒤로가기 후엔 저장된 ID 가 안 채워져 "풀린 것처럼" 보였음.
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    const remembered = rememberedCredentialStore.read();
    if (!remembered) {
      return;
    }
    setCredentials((prev) => {
      if (prev.userId || prev.password) {
        return prev;
      }
      return { userId: remembered.userId, password: remembered.password };
    });
  }, [isAuthenticated, setCredentials]);

  const onChangeRememberCredentials = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const next = e.target.checked;
    setRememberCredentials(next);
    // OFF 로 끄면 저장된 자격증명을 즉시 삭제 — 저장 해제는 즉시 반영.
    if (!next) {
      rememberedCredentialStore.clear();
    } else if (credentials.userId && credentials.password) {
      // ON 으로 켤 때 입력값이 있으면 바로 저장(다음 로그인 시에도 다시 저장됨).
      rememberedCredentialStore.save(credentials.userId, credentials.password);
    }
  };

  const onClickLoginButton = () => {
    if (!credentials.userId || !credentials.password) {
      return;
    }

    // 로그인 버튼 클릭(사용자 제스처) 시 바로 전체화면 진입 — 콕핏 몰입 모드.
    // (모니터 개수대로 외부 디스플레이 분할은 콕핏의 외부 디스플레이 매니저가
    //  담당; 여기서는 메인 창 전체화면만 요청. requestFullscreen 은 제스처 필요.)
    requestAppFullscreen();

    // 같은 제스처 컨텍스트에서 오디오도 풀어둔다. 주행 중에 "소리를 켜려면 화면을
    // 클릭하세요" 안내를 띄우지 않으려면 여기가 유일하게 확실한 시점이다.
    unlockCockpitAudio();

    // 같은 제스처 컨텍스트에서 다중 모니터 점유 + 마이크/휠 권한 일괄 획득.
    props.onLoginGesture?.();

    setSelectionError(null);

    // 로그인 직전에 "ID·비밀번호 저장" 설정에 따라 영구 저장/삭제.
    if (rememberCredentials) {
      rememberedCredentialStore.save(credentials.userId, credentials.password);
    } else {
      rememberedCredentialStore.clear();
    }

    login(credentials, {
      persist: true,
      clearStoredSessionOnFailure: false,
    });
  };


  const onClickLogoutButton = () => {
    clearStoredSession();

    const finalizeLogout = () => {
      props.bridge.disconnectSignaling();
      props.bridge.disconnectMqtt();
      resetLoginState();
      setSelectedVehicleId("");
      setSelectionError(null);
      // 재로그인 시 장치 준비 단계를 다시 거치도록 리셋.
      setSetupDone(false);
    };

    props.bridge
      .disconnectVehicleSession()
      .catch((err) => {
        console.error("[LoginScreen] Vehicle session disconnect failed:", err);
      })
      .finally(() => {
        finalizeLogout();
      });
  };

  const onClickConnectButton = (vehicleIdOverride?: string) => {
    const vehicleId = vehicleIdOverride ?? selectedVehicleId;
    const vehicle = vehicleIdOverride
      ? vehicleList.find((v) => v.vehicleId === vehicleIdOverride) ?? selectedVehicle
      : selectedVehicle;

    if (!vehicleId || !vehicle?.selectable) {
      return;
    }

    // 자동 로그인으로 로그인 버튼을 건너뛴 경우 여기가 첫 제스처가 된다 —
    // 두 지점 모두에서 풀어야 어느 경로로 들어와도 소리가 난다.
    unlockCockpitAudio();

    props.bridge
      .connectVehicleSession(vehicleId)
      .then(() => {
        setSelectionError(null);
        props.onVehicleSessionConnected?.();
      })
      .catch((err) => {
        console.error("[LoginScreen] Vehicle session connect failed:", err);
        setSelectionError(err?.message || "Vehicle connection failed");
        showCockpitToast(`차량 연결에 실패했습니다: ${err.message}`, { alert: true });
      });
  };

  const onChangeUserId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({
      ...prev,
      userId: e.target.value,
    }));
  };

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({
      ...prev,
      password: e.target.value,
    }));
  };

  const pressKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onClickLoginButton();
    }
  };

  // 휠 CONFIRM 으로 로그인 트리거(저장된 자격증명이 프리필된 경우 키보드 불필요).
  // onClickLoginButton 은 자격증명이 비어있으면 자체적으로 no-op 이므로 안전.
  // 최신 핸들러를 ref 로 참조 — 휠 콜백이 항상 현재 클로저를 호출.
  const onClickLoginButtonRef = useRef<() => void>(() => {});
  onClickLoginButtonRef.current = onClickLoginButton;

  const handleLoginWheelEvent = useCallback((event: WheelMenuEvent) => {
    if (event === "CONFIRM") {
      onClickLoginButtonRef.current();
    }
  }, []);

  // 인증 전에만 로그인 화면 휠 내비게이션 활성화(인증 후엔 FleetSelect 가 담당).
  useWheelMenuNav({
    enabled: !isAuthenticated,
    onEvent: handleLoginWheelEvent,
    onConnectionChange: setWheelConnected,
  });

  const shouldShowEmptyState =
    isAuthenticated &&
    signalingStatus !== "loading" &&
    mqttStatus !== "loading" &&
    vehicleList.length === 0;

  // Authenticated but device setup not yet completed: run the device-readiness step
  // between login and fleet selection. DeviceSetup auto-advances (onComplete) when
  // everything is already ready, so returning operators aren't trapped on this page.
  if (isAuthenticated && !setupDone) {
    return (
      <DeviceSetup
        loginId={loginId}
        onComplete={() => setSetupDone(true)}
        onLogout={onClickLogoutButton}
        displaysSupported={Boolean(props.displaysSupported)}
        displaysReady={Boolean(props.displaysReady)}
        displayStatuses={props.displayStatuses ?? []}
        onOpenDisplays={() => {
          requestAppFullscreen();
          props.onOpenDisplays?.();
        }}
      />
    );
  }

  // Authenticated branch: render full-screen fleet selection view.
  if (isAuthenticated) {
    return (
      <FleetSelect
        vehicleList={vehicleList}
        signalingStatus={signalingStatus}
        mqttStatus={mqttStatus}
        signalingError={signalingError}
        mqttError={mqttError}
        selectionError={selectionError}
        shouldShowEmptyState={shouldShowEmptyState}
        selectedVehicleId={selectedVehicleId}
        loginId={loginId}
        onLogout={onClickLogoutButton}
        onReload={() => {
          // 새로고침 = "로그아웃 후 재로그인"과 동급: 시그널링/MQTT 세션을 완전히 끊고
          // (자격증명은 유지) 다시 로그인한 뒤 차량 목록을 갱신한다. (단순 목록 reload 아님)
          void (async () => {
            try {
              await props.bridge.disconnectVehicleSession().catch(() => {});
              props.bridge.disconnectSignaling();
              props.bridge.disconnectMqtt();
              // signaling 이 연결돼야 MQTT 로그인이 진행되므로 순차 실행.
              await props.bridge.loginSignaling();
              await props.bridge.loginMqtt();
            } catch (err) {
              console.error("[LoginScreen] 새로고침(재연결) 실패:", err);
            } finally {
              void reload();
            }
          })();
        }}
        onSelectVehicle={setSelectedVehicleId}
        onConnect={() => onClickConnectButton()}
        onSelectAndConnect={(vehicleId) => {
          setSelectedVehicleId(vehicleId);
          onClickConnectButton(vehicleId);
        }}
      />
    );
  }

  // Unauthenticated branch: original login form, unchanged.
  return (
    <div className="login-view show">
      {/* 배포된 UI 가 방금 고친 그 버전인지 확인하고, 아니면 바로 다시 받는다.
          캐시된 예전 번들 때문에 "고쳤는데 안 바뀐다"로 헤매는 걸 막는다. */}
      <div className="login-corner">
        <button
          type="button"
          className="login-corner__reload"
          onClick={reloadUi}
          title="새로고침 — 최신 UI 를 다시 받습니다"
          aria-label="새로고침"
          data-testid="login-reload"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M13.5 8a5.5 5.5 0 1 1-1.61-3.89"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <path d="M13.6 1.9v3.2h-3.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="login-corner__ver" data-testid="login-ui-version">
          v{UI_VERSION}
        </span>
      </div>
      <div className="login-dialog">
        <div className="login-brand">
          <div className="login-brand-mark-wrap">
            <img
              className="login-brand-mark"
              src="asset/AW_icon_black.png"
              alt="ACEWORKS"
            />
          </div>
          <div className="login-brand-text">
            <h1 className="login-title">Remote Cockpit</h1>
            <p className="login-sub">원격주행 콕핏 · 계정으로 로그인</p>
          </div>
        </div>
        <hr className="login-divider" aria-hidden="true" />
        {/* iter 05: autocomplete attributes — browsers surface saved credentials correctly */}
        <label className="login-field-label" htmlFor="username">ID</label>
        <input
          id="username"
          placeholder="Enter your ID"
          value={credentials.userId}
          onChange={onChangeUserId}
          onKeyUp={pressKey}
          autoComplete="username"
          aria-label="사용자 ID"
        />
        <label className="login-field-label" htmlFor="password">비밀번호</label>
        <input
          id="password"
          type="password"
          placeholder="Enter password"
          value={credentials.password}
          onChange={onChangePassword}
          onKeyUp={pressKey}
          autoComplete="current-password"
          aria-label="비밀번호"
        />
        {/* iter 04: aria-live container always in DOM so AT announces errors on update */}
        <div
          className="login-error-region"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {loginError && (
            <p className="login-error" data-testid="login-error">
              {loginError}
            </p>
          )}
        </div>
        {/* iter 02: login-remember — label wraps checkbox for max click area; custom focus ring via CSS */}
        <label className="login-remember">
          <input
            type="checkbox"
            data-testid="login-remember"
            checked={rememberCredentials}
            onChange={onChangeRememberCredentials}
          />
          <span>ID·비밀번호 저장</span>
        </label>
        <div className="login-button">
          {/* iter 03: loading state — spinner span + aria-busy for AT */}
          <button
            type="button"
            className={`aw-login-btn primary${isLoggingIn ? " loading" : ""}`}
            disabled={isLoggingIn}
            onClick={onClickLoginButton}
            aria-busy={isLoggingIn}
          >
            {isLoggingIn ? (
              <>
                <span className="aw-login-spinner" aria-hidden="true" />
                <span>logging in...</span>
              </>
            ) : (
              "login"
            )}
          </button>
        </div>
        {/* 보조 화면 펼치기 버튼은 로그인 후 "장치 준비" 스텝 페이지로 이관 — 로그인 화면엔 미표시. */}
        {/* 휠 사용 안내 — 저장된 자격증명이 있으면 휠만으로 로그인 가능.
            없으면 ID/비밀번호는 키보드 입력 후 휠 가속페달/버튼으로 로그인. */}
        <p className="login-wheel-guide">
          {rememberCredentials
            ? "게이밍 휠 가속페달 또는 X/A 버튼으로 바로 로그인할 수 있습니다."
            : "ID·비밀번호를 입력한 뒤 휠 가속페달 또는 X/A 버튼으로 로그인할 수 있습니다."}
        </p>
      </div>

      {/* 휠/게임패드 조작 안내 오버레이 */}
      <WheelHelpOverlay
        connected={wheelConnected}
        contextHint="가속페달/X/A: 로그인 (저장된 계정 시 키보드 불필요)"
      />
    </div>
  );
};

LoginScreenComponent.displayName = "LoginScreen";

export const LoginScreen = React.memo(LoginScreenComponent);
